// Modal para invocar un agente IA. Carga la lista de agentes disponibles
// del backend y muestra el form dinámico según los inputs declarados.

import React, { useEffect, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import {
  FaTimes,
  FaRobot,
  FaUserCircle,
  FaProjectDiagram,
  FaHistory,
  FaLightbulb,
  FaFileAlt,
  FaPlay,
  FaCheckCircle,
  FaExclamationCircle,
} from 'react-icons/fa';
import {
  TechButton,
  TechInput,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import { listAgents, runAgent } from '../../services/investigationService';

const AGENT_ICONS = {
  entity_researcher: <FaUserCircle />,
  connection_finder: <FaProjectDiagram />,
  timeline_builder: <FaHistory />,
  theory_evaluator: <FaLightbulb />,
  report_synthesizer: <FaFileAlt />,
};

const Backdrop = styled.div`
  position: fixed; inset: 0; z-index: 1400;
  background: rgba(8, 14, 24, 0.7); backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center; padding: 1rem;
`;
const Card = styled.div`
  width: min(720px, 100%); max-height: 88vh; overflow-y: auto;
  background: var(--color-primary);
  border: 1px solid rgba(0, 180, 216, 0.35);
  border-radius: 12px;
  padding: 1.4rem 1.5rem;
  position: relative;
  color: var(--text-secondary);

  h2 { color: var(--text-primary); margin: 0 0 1rem; display: flex; align-items: center; gap: 0.5rem; }
  label {
    display: block; font-family: ${mono};
    font-size: 0.62rem; letter-spacing: 0.18em; text-transform: uppercase;
    color: var(--text-disabled); margin: 0.7rem 0 0.3rem;
  }
  textarea, select {
    width: 100%;
    background: rgba(11, 19, 32, 0.65);
    border: 1px solid ${borderSoft};
    border-radius: 7px;
    color: var(--text-primary);
    font-family: inherit;
    padding: 0.55rem 0.75rem;
    font-size: 0.88rem;
    outline: none;
    resize: vertical;
    &:focus { border-color: rgba(0, 180, 216, 0.5); }
  }
  textarea { min-height: 80px; }
  .close {
    position: absolute; top: 12px; right: 14px;
    background: transparent; border: 1px solid ${borderSoft};
    color: var(--text-disabled);
    width: 28px; height: 28px; border-radius: 6px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    &:hover { color: var(--alert-error); }
  }
  .actions { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1.1rem; }
`;

const AgentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.65rem;
`;

const AgentCard = styled.button`
  text-align: left;
  background: ${({ $selected }) => ($selected ? 'rgba(0, 180, 216, 0.1)' : panelDeep)};
  border: 1px solid ${({ $selected }) => ($selected ? 'rgba(0, 180, 216, 0.5)' : borderSoft)};
  border-radius: 8px;
  padding: 0.85rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  transition: all 0.18s ease;
  &:hover { border-color: rgba(0, 180, 216, 0.4); background: rgba(0, 180, 216, 0.05); }

  .head { display: flex; align-items: center; gap: 0.55rem; }
  .icon {
    width: 30px; height: 30px;
    border-radius: 50%;
    background: rgba(0, 180, 216, 0.12);
    border: 1px solid rgba(0, 180, 216, 0.3);
    color: ${accent};
    display: flex; align-items: center; justify-content: center;
  }
  .label { color: var(--text-primary); font-weight: 600; font-size: 0.92rem; }
  .desc { color: var(--text-secondary); font-size: 0.81rem; line-height: 1.5; }
`;

const ResultBox = styled.div`
  background: rgba(11, 19, 32, 0.5);
  border: 1px solid ${borderSoft};
  border-left: 3px solid var(--alert-success);
  border-radius: 8px;
  padding: 0.85rem 1rem;
  margin-top: 1rem;
  color: var(--text-secondary);
  font-size: 0.86rem;
  line-height: 1.55;

  pre {
    background: rgba(0, 0, 0, 0.25);
    border-radius: 6px;
    padding: 0.5rem 0.7rem;
    font-size: 0.78rem;
    overflow-x: auto;
    margin-top: 6px;
  }
  .head {
    display: flex; align-items: center; gap: 0.5rem;
    color: var(--text-primary);
    font-weight: 600;
    margin-bottom: 6px;
  }
`;

export default function AgentLauncher({ slug, onClose, onCompleted }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [values, setValues] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    listAgents(slug)
      .then((list) => {
        setAgents(list);
        if (list.length) setSelected(list[0]);
      })
      .catch((e) => setErr(friendlyError(e)))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    // Reset values cuando cambia el agente
    if (!selected) return;
    const init = {};
    for (const inp of selected.inputs || []) {
      if (inp.default !== undefined) init[inp.name] = inp.default;
    }
    setValues(init);
    setResult(null);
    setErr(null);
  }, [selected]);

  const updateValue = (name, val) => setValues((v) => ({ ...v, [name]: val }));

  const submit = async () => {
    if (!selected) return;
    // Validar requeridos
    for (const inp of selected.inputs || []) {
      if (inp.required && !values[inp.name]) {
        setErr(`Campo requerido: ${inp.label}`);
        return;
      }
    }
    setRunning(true);
    setErr(null);
    try {
      const r = await runAgent(slug, selected.name, values);
      setResult(r);
      if (r.ok && onCompleted) onCompleted(r);
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Backdrop onClick={onClose}>
      <Card onClick={(e) => e.stopPropagation()}>
        <button type="button" className="close" onClick={onClose}><FaTimes /></button>
        <h2><FaRobot style={{ color: accent }} /> Agentes de investigación</h2>

        {loading ? (
          <div style={{ color: 'var(--text-disabled)' }}>Cargando agentes…</div>
        ) : (
          <>
            <AgentGrid>
              {agents.map((a) => (
                <AgentCard
                  key={a.name}
                  $selected={selected?.name === a.name}
                  onClick={() => setSelected(a)}
                >
                  <div className="head">
                    <div className="icon">{AGENT_ICONS[a.name] || <FaRobot />}</div>
                    <div className="label">{a.label}</div>
                  </div>
                  <div className="desc">{a.description}</div>
                </AgentCard>
              ))}
            </AgentGrid>

            {selected && (
              <>
                <div style={{ marginTop: 18, fontFamily: mono, fontSize: '0.62rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: accent }}>
                  Inputs · {selected.label}
                </div>
                {(selected.inputs || []).map((inp) => (
                  <div key={inp.name}>
                    <label>{inp.label}{inp.required ? ' *' : ''}</label>
                    {inp.type === 'textarea' ? (
                      <textarea
                        value={values[inp.name] || ''}
                        onChange={(e) => updateValue(inp.name, e.target.value)}
                      />
                    ) : inp.type === 'select' ? (
                      <select
                        value={values[inp.name] || ''}
                        onChange={(e) => updateValue(inp.name, e.target.value)}
                      >
                        {(inp.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    ) : inp.type === 'number' ? (
                      <TechInput
                        type="number"
                        value={values[inp.name] ?? ''}
                        onChange={(e) => updateValue(inp.name, Number(e.target.value))}
                      />
                    ) : (
                      <TechInput
                        value={values[inp.name] || ''}
                        onChange={(e) => updateValue(inp.name, e.target.value)}
                      />
                    )}
                  </div>
                ))}
                {err && <ErrorBox style={{ marginTop: 12 }}>{err}</ErrorBox>}
                {result && (
                  <ResultBox>
                    <div className="head">
                      {result.ok ? <FaCheckCircle style={{ color: 'var(--alert-success)' }} /> : <FaExclamationCircle style={{ color: 'var(--alert-error)' }} />}
                      {result.ok ? 'Agente terminó' : 'Agente falló'}
                    </div>
                    {result.error && <div style={{ color: 'var(--alert-error)' }}>{result.error}</div>}
                    {result.summary && (
                      <pre>{JSON.stringify(result.summary, null, 2)}</pre>
                    )}
                    {result.ok && (
                      <div style={{ marginTop: 6, color: 'var(--text-disabled)', fontSize: '0.78rem' }}>
                        Output guardado en {result.output_type} #{result.output_id}.
                        Cerrá este modal para ver los cambios en la investigación.
                      </div>
                    )}
                  </ResultBox>
                )}
                <div className="actions">
                  <TechButton onClick={onClose} disabled={running} style={{ background: 'transparent', color: 'var(--text-secondary)' }}>
                    Cerrar
                  </TechButton>
                  <TechButton onClick={submit} disabled={running}>
                    <FaPlay style={{ marginRight: 6 }} />
                    {running ? 'Corriendo (puede tardar 30-90s)…' : 'Ejecutar agente'}
                  </TechButton>
                </div>
              </>
            )}
          </>
        )}
      </Card>
    </Backdrop>
  );
}
